/**
 * Crossed-book handling for the live order book.
 *
 * A negative spread (best bid above best ask) cannot persist in a matched
 * market — sustained crossing means our local mirror of the book has drifted
 * (e.g. a delete event was missed across a reconnect). Two responses:
 * display-side, never show a negative spread; sync-side, re-seed the mirror
 * from the REST snapshot once the crossing has persisted long enough to rule
 * out a transient mid-trade frame.
 */

/** Crossing must persist this long before a re-seed — one-frame mid-trade crossings self-correct. */
export const CROSSED_RESEED_DELAY_MS = 2_000;

/** Minimum gap between re-seeds, so a flapping or genuinely crossed upstream book can't hammer the API. */
export const RESEED_COOLDOWN_MS = 10_000;

/** A book is crossed when its spread is known and negative. */
export function isCrossedSpread(spread: number | undefined): boolean {
  return spread != null && spread < 0;
}

/**
 * Spread safe to display: a crossed (negative) spread renders as unknown (`—`)
 * rather than a nonsense negative price.
 */
export function sanitizeSpread(spread: number | undefined): number | undefined {
  return isCrossedSpread(spread) ? undefined : spread;
}
